import { MiddlewareState, Placement, arrow, autoPlacement, computePosition, flip, offset, platform, shift, size } from '@floating-ui/dom';
import { offsetParent } from 'composed-offset-position';
import { LitElement, ReactiveController, ReactiveControllerHost } from 'lit';
import { isPopoverSupported } from '../util/popover';

export class AutoPositionController {
  private host: ReactiveControllerHost;
  constructor(host: ReactiveControllerHost) {
    (this.host = host).addController(this as ReactiveController);
  }

  /**
   * Add dynamic position to the floating panel.
   * 1) floating element
   * 2) sync width and height if needed
   * 3) Position of the dropdown panel
   * 4) Hoisting to overflow the container
   * 5) offet gap between the trigger element and floating element
   * 6) trigger element to toggle the panel
   * 7) positions to place the panel
   * 8) ArrowElement reference to show along with the tooltip
   */
  autoPosition(
    contentElement: HTMLElement /* 1 */,
    sync?: string /* 2 */,
    position?: Placement | 'auto' /* 3 */,
    isInline?: boolean /* 4 */,
    gap?: number /* 5 */,
    triggerEle?: HTMLElement /* 6 */,
    allowedPlacements?: Placement[] /* 7 */,
    arrowElement?: HTMLElement /* 8 */,
    isFlipAllowed = false
  ) {
    if (!contentElement) {
      return;
    }
    const strategy = !isInline ? 'fixed' : 'absolute';
    contentElement.style.position = strategy;
    const host = triggerEle ? triggerEle : (this.host as LitElement);
    //Workaround for this issue:https://github.com/floating-ui/floating-ui/issues/2858
    const isPopoverEnabled = !isInline && isPopoverSupported();
    let shiftMiddleware = shift();
    if (isPopoverEnabled) {
      shiftMiddleware = shift({ boundary: [] });
    } else if (isInline && arrowElement) {
      //used for inline overlays with precise positioning
      shiftMiddleware = shift({ mainAxis: false });
    }

    const floatingArrow = arrowElement ? arrow({ element: arrowElement, padding: 16 }) : null;
    const middleware = [
      // The offset middleware goes first
      offset({ mainAxis: 0, crossAxis: 0 })
    ];
    // First we sync width/height
    if (sync) {
      middleware.push(
        size({
          apply: ({ rects }: MiddlewareState) => {
            const syncWidth = sync === 'width' || sync === 'both';
            const syncHeight = sync === 'height' || sync === 'both';
            contentElement.style.width = syncWidth ? `${rects.reference.width}px` : '';
            contentElement.style.height = syncHeight ? `${rects.reference.height}px` : '';
          }
        })
      );
    } else {
      // Cleanup styles if we're not matching width/height
      contentElement.style.width = '';
      contentElement.style.height = '';
    }
    if (position === 'auto') {
      middleware.push(offset(gap), autoPlacement({ allowedPlacements: allowedPlacements }), shiftMiddleware, floatingArrow!);
    } else if (isFlipAllowed) {
      middleware.push(offset(gap), flip(), shiftMiddleware, floatingArrow!);
    } else {
      middleware.push(offset(gap), shiftMiddleware, floatingArrow!);
    }
    const getOffsetParent =
      strategy === 'absolute' ? (element: Element) => platform.getOffsetParent(element, offsetParent) : platform.getOffsetParent;
    computePosition(host, contentElement, {
      placement: position === 'auto' ? 'bottom-start' : position,
      middleware: middleware,
      strategy: strategy,
      platform: {
        ...platform,
        getOffsetParent
      }
    }).then(({ x, y, placement, middlewareData }) => {
      const isRtl = platform.isRTL(contentElement);

      // Browser's viewport (including scrollbar) - visible content display (excluding scrollbar)
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;

      // Determine whether document is inside iFrame. Workaround for https://github.com/floating-ui/floating-ui/issues/3200
      const isInsideIframe = window.frameElement !== null;

      // Negating the width of the scrollbar from the calculated width in case RTL is enabled and document is inside IFrame
      const adjustedX = isRtl && scrollBarWidth > 0 && isInsideIframe ? x - scrollBarWidth : x;

      Object.assign(contentElement.style, {
        left: `0px`,
        top: `0px`,
        transform: `translate(${adjustedX}px, ${y}px)`
      });
      if (arrowElement) {
        // below if block Resets styles
        if (arrowElement.style) {
          arrowElement.style.top = ''; //setting up previous top value;
          arrowElement.style.bottom = '';
          arrowElement.style.left = '';
          arrowElement.style.right = '';
          arrowElement.style.insetInlineStart = '';
          arrowElement.style.insetInlineEnd = '';
        }
        const { x: arrowX, y: arrowY } = middlewareData.arrow ?? {}; // get positions of arrow from middleware
        const staticSide: string | undefined = {
          top: 'bottom',
          right: 'left',
          bottom: 'top',
          left: 'right'
        }[placement.split('-')[0]]; // get the opposite of the placement to apply -4px
        let arrowPlacement: string | null | undefined = null;
        let arrowOffset: number | null = null;
        const [mainPlacement, subPlacement] = placement.split('-'); //split the placement positions
        const isVerticalPlacement = ['bottom', 'top'].includes(mainPlacement) && subPlacement;
        const isHorizontalPlacement = ['left', 'right'].includes(mainPlacement) && subPlacement;
        // If the tooltip position has bottom and top, then offset will be done in left and right positons for arrow.
        if (isVerticalPlacement) {
          arrowPlacement = { start: 'inset-inline-start', end: 'inset-inline-end' }[subPlacement];
          arrowOffset = 16;
        } else if (isHorizontalPlacement) {
          arrowPlacement = { start: 'top', end: 'bottom' }[subPlacement];
          arrowOffset = 16;
        }

        Object.assign(arrowElement.style, {
          left: !isVerticalPlacement ? `${arrowX ?? ''}px` : '',
          top: !isHorizontalPlacement ? `${arrowY ?? ''}px` : '',
          ...(arrowPlacement ? { [arrowPlacement]: `${arrowOffset ?? ''}px` } : {}),
          ...(staticSide ? { [staticSide]: '-4px' } : {})
        });
      }
    });
  }
}
